// Zoom and pan of the viewer's picture. At fit the picture is as large as the stage allows (never
// above its own pixel size), centred; zooming scales it about its centre and pans it by an offset.
// Lengths are CSS pixels, points are relative to the stage's centre.

export interface Size {
    width: number;
    height: number;
}

export interface Point {
    x: number;
    y: number;
}

export interface ZoomPan {
    /** 1 is fit. */
    scale: number;
    x: number;
    y: number;
}

/** What a zoom is bounded by. */
export interface ZoomBounds {
    /** The picture's box at fit. */
    fit: Size;
    stage: Size;
    maxScale: number;
}

export const FIT_VIEW: ZoomPan = {scale: 1, x: 0, y: 0};

/** The smallest zoom limit; pictures with many pixels go further (see `maxZoomScale`). */
const MIN_MAX_SCALE = 8;
/** How many times its own pixel size a picture may be zoomed to. */
const MAX_PIXEL_ZOOM = 4;
/** The double tap / double click zooms at least this far, even into a small picture. */
const MIN_DETAIL_SCALE = 2;
/** A finger's double tap stops here: a phone's 100% of a big photo is far too close. */
const MAX_TOUCH_DETAIL_SCALE = 3;
/** A zoom ending below this goes back to fit: a pinch rarely lands exactly on it. */
const SNAP_TO_FIT_SCALE = 1.05;

function clamp(value: number, min: number, max: number): number {
    // + 0 turns a -0 (from clamping to the bounds [-0, 0]) into 0.
    return Math.min(max, Math.max(min, value)) + 0;
}

/** The picture's box at fit: it fills the stage keeping its ratio, but is never enlarged past its pixel size. */
export function fitSize(image: Size | null, stage: Size): Size {
    if (!image || image.width <= 0 || image.height <= 0) return stage;
    const k = Math.min(stage.width / image.width, stage.height / image.height, 1);
    return {width: image.width * k, height: image.height * k};
}

/** The zoom limit: 8× the fit size, or 4× the picture's own pixel size when that is further. */
export function maxZoomScale(image: Size | null, fit: Size): number {
    if (!image || fit.width <= 0) return MIN_MAX_SCALE;
    return Math.max(MIN_MAX_SCALE, MAX_PIXEL_ZOOM * image.width / fit.width);
}

export function zoomBounds(image: Size | null, stage: Size): ZoomBounds {
    const fit = fitSize(image, stage);
    return {fit, stage, maxScale: maxZoomScale(image, fit)};
}

/** Keeps a picture larger than the stage covering it, and one smaller than it centred, on each axis. */
export function clampPan(view: ZoomPan, {fit, stage}: ZoomBounds): ZoomPan {
    const limitX = Math.max(0, (fit.width * view.scale - stage.width) / 2);
    const limitY = Math.max(0, (fit.height * view.scale - stage.height) / 2);
    return {scale: view.scale, x: clamp(view.x, -limitX, limitX), y: clamp(view.y, -limitY, limitY)};
}

/**
 * A zoom set for a fit of size `from`, carried over to the fit of `bounds` (e.g. after the stage was
 * resized or a side panel opened): the picture keeps its size on screen, about the same point. At
 * fit it stays at fit, following the stage.
 */
export function refit(view: ZoomPan, from: Size, bounds: ZoomBounds): ZoomPan {
    const to = bounds.fit;
    if (view.scale <= 1 || from.width <= 0 || to.width <= 0 || from.width === to.width) return view;
    const scale = clamp(view.scale * from.width / to.width, 1, bounds.maxScale);
    // The offsets grow with the picture when it can't keep its size (at the limits).
    const k = scale * to.width / (view.scale * from.width);
    return {scale, x: view.x * k, y: view.y * k};
}

/**
 * Zooms `start` to `scale`, moving the picture's point that was under `from` to `to` (the two
 * fingers' midpoint at the start and now, for a pinch; the same point for the wheel).
 */
export function zoomAround(start: ZoomPan, scale: number, from: Point, to: Point, bounds: ZoomBounds): ZoomPan {
    const s = clamp(scale, 1, bounds.maxScale);
    const k = s / start.scale;
    return clampPan({scale: s, x: to.x - (from.x - start.x) * k, y: to.y - (from.y - start.y) * k}, bounds);
}

/** Zooms to `scale` keeping the picture's point under `anchor` in place. */
export function zoomAt(start: ZoomPan, scale: number, anchor: Point, bounds: ZoomBounds): ZoomPan {
    return zoomAround(start, scale, anchor, anchor, bounds);
}

/**
 * Where a finished zoom gesture settles: barely zoomed in is taken for fit, which would otherwise
 * stop swipes and fetch the original.
 */
export function snapToFit(view: ZoomPan): ZoomPan {
    return view.scale < SNAP_TO_FIT_SCALE ? FIT_VIEW : view;
}

/** Moves the picture by a drag of (`dx`, `dy`) from `start`. */
export function panBy(start: ZoomPan, dx: number, dy: number, bounds: ZoomBounds): ZoomPan {
    return clampPan({scale: start.scale, x: start.x + dx, y: start.y + dy}, bounds);
}

/** The zoom showing the picture at its own pixel size (100%); 1 when its size is unknown. */
export function pixelScale(image: Size | null, fit: Size): number {
    return image && fit.width > 0 ? image.width / fit.width : 1;
}

/** Where a double click zooms to: 100%, but at least 2× (and, for a finger's double tap, at most 3×). */
export function detailScale(image: Size | null, bounds: ZoomBounds, touch: boolean): number {
    const scale = Math.max(MIN_DETAIL_SCALE, pixelScale(image, bounds.fit));
    return Math.min(scale, bounds.maxScale, touch ? MAX_TOUCH_DETAIL_SCALE : Infinity);
}

/** The zoom as a percentage of the picture's own pixel size (of the fit size when that is unknown). */
export function zoomPercent(scale: number, image: Size | null, fit: Size): number {
    return Math.round(scale / pixelScale(image, fit) * 100);
}

/**
 * The zoom factor of one wheel event. A trackpad pinch arrives as a wheel event with `ctrlKey` and
 * small deltas, so it is amplified; line and page deltas are converted to pixels first.
 */
export function wheelZoomFactor(deltaY: number, deltaMode: number, ctrlKey: boolean): number {
    const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 800 : deltaY;
    return Math.exp(-pixels * (ctrlKey ? 0.01 : 0.002));
}
